// ═══════════════════════════════════════════════════════════════════
//  modules/hub/HubConsole.js
//  Une console RCON pour le Hub — et pour le Hub seulement.
//
//  La Console générale de l'intranet envoie chaque commande AUX DEUX
//  serveurs à la fois. Sur une base partagée, une commande `lp` s'exécute
//  donc deux fois en parallèle et laisse des lignes en double, que plus
//  aucune commande ne peut ensuite corriger. Celle-ci parle à un serveur.
// ═══════════════════════════════════════════════════════════════════

import { useRef, useState } from "react";
import { THEME } from "../../socle/theme";

export function HubConsole({ api, configure }) {
  const [commande, setCommande] = useState("");
  const [lignes, setLignes] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const [historique, setHistorique] = useState([]);
  const [curseur, setCurseur] = useState(-1);
  const champ = useRef(null);

  const ajouter = (type, texte) =>
    setLignes((l) => [...l, { type, texte, t: new Date().toLocaleTimeString("fr-FR") }].slice(-200));

  async function envoyerCommande(e) {
    if (e) e.preventDefault();
    const c = commande.trim();
    if (!c || envoi) return;
    setHistorique((h) => [c, ...h.filter((x) => x !== c)].slice(0, 50));
    setCurseur(-1);
    setCommande("");
    ajouter("envoi", c);
    setEnvoi(true);
    try {
      const r = await api.console(c);
      if (r.asynchrone) {
        // Le silence de LuckPerms n'est PAS un échec. Sans cette ligne,
        // on le prend pour une panne et on retape la commande — ce qui,
        // sur une base partagée, est exactement le geste à ne pas faire.
        ajouter("note",
          "Aucune réponse — c'est normal : LuckPerms exécute ses commandes en asynchrone, "
          + "le paquet RCON revient avant qu'elle existe. La commande est très probablement "
          + "passée. Ne la retape pas : vérifie en base (POSER_PERMISSIONS.bat, choix 3).");
      } else {
        ajouter("reponse", r.reponse || "(le serveur n'a rien répondu)");
      }
    } catch (err) {
      ajouter("erreur", err.message);
    } finally {
      setEnvoi(false);
      if (champ.current) champ.current.focus();
    }
  }

  // Flèches haut et bas : l'historique de la session. Pas de stockage —
  // une console qui se souvient d'hier est une console qui rejoue hier.
  function touche(e) {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      const i = Math.min(curseur + 1, historique.length - 1);
      if (i >= 0) { setCurseur(i); setCommande(historique[i]); }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const i = curseur - 1;
      setCurseur(i);
      setCommande(i >= 0 ? historique[i] : "");
    }
  }

  const couleur = (t) =>
    t === "envoi" ? THEME.accent
      : t === "erreur" ? THEME.red
        : t === "note" ? THEME.amber
          : THEME.text;

  if (!configure) {
    return (
      <div style={{
        padding: "16px 18px", borderRadius: 10, fontSize: 13.5, lineHeight: 1.6,
        background: THEME.amberDim, border: `1px solid ${THEME.amber}`, color: THEME.text,
      }}>
        <b>La console a besoin de l'adresse RCON du Hub.</b>
        <div style={{ marginTop: 8, color: THEME.textMuted }}>
          Ajoute <code>RCON_HUB_HOST</code>, <code>RCON_HUB_PORT</code> et
          <code> RCON_HUB_PASSWORD</code> au <code>.env</code> du backend, puis redémarre
          le service <code>OrialisIntranet</code>. Voir l'onglet Suivi.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        background: THEME.bg, border: `1px solid ${THEME.border}`, borderRadius: 12,
        padding: 14, height: 420, overflowY: "auto",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 12.5,
      }}>
        {lignes.length === 0 && (
          <div style={{ color: THEME.textDim, lineHeight: 1.7 }}>
            Console du <b style={{ color: THEME.textMuted }}>Hub</b>. Une commande part sur ce
            serveur, et sur lui seul.
            <br /><br />
            <span style={{ color: THEME.textMuted }}>op</span>,
            <span style={{ color: THEME.textMuted }}> deop</span>,
            <span style={{ color: THEME.textMuted }}> stop</span> et
            <span style={{ color: THEME.textMuted }}> restart</span> sont refusées : les
            opérateurs ont été retirés des trois serveurs le 2 août, et l'arrêt se fait au
            panneau, qui sait rallumer.
          </div>
        )}
        {lignes.map((l, i) => (
          <div key={i} style={{ marginBottom: 7, color: couleur(l.type), whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            <span style={{ color: THEME.textDim, marginRight: 8 }}>{l.t}</span>
            {l.type === "envoi" ? "› " : ""}{l.texte}
          </div>
        ))}
      </div>

      <form onSubmit={envoyerCommande} style={{ display: "flex", gap: 10, marginTop: 12 }}>
        <input
          ref={champ}
          value={commande}
          onChange={(e) => setCommande(e.target.value)}
          onKeyDown={touche}
          placeholder="commande, sans le /"
          disabled={envoi}
          style={{
            flex: 1, background: THEME.surface, color: THEME.text,
            border: `1px solid ${THEME.border}`, borderRadius: 10, padding: "10px 14px",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 13,
            outline: "none",
          }}
        />
        <button type="submit" disabled={envoi || !commande.trim()}
          style={{
            background: envoi || !commande.trim() ? THEME.surface : THEME.accent,
            color: envoi || !commande.trim() ? THEME.textMuted : "#fff",
            border: `1px solid ${envoi || !commande.trim() ? THEME.border : THEME.accent}`,
            borderRadius: 10, padding: "10px 22px", fontSize: 13, fontWeight: 600,
            cursor: envoi || !commande.trim() ? "default" : "pointer",
          }}>
          {envoi ? "…" : "Envoyer"}
        </button>
      </form>
    </div>
  );
}
